<template>
    <ValidationObserver ref="form" v-slot="{ invalid }">
        <div class="absolute top-0 left-0 right-0 bottom-0 w-full overflow-hidden flex items-center text-center justify-center z-50" v-show="loading"><spinner line-fg-color="#922C49" size="55" :line-size="5"></spinner></div>
        <form id="form" v-if="!loading" @submit.prevent="submitForm" class="flex flex-col h-full" autocomplete="off">
            <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
                <template slot="title">{{ type }} User</template>
            </page-header>

            <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" role="alert" v-show="invalid && submitted" >Please complete all required fields</div>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" v-if="errorMessage">{{ errorMessage }}</div>

            <div class="flex md:flex-row sm:flex-col flex-grow">
                <div class="md:w-3/4 sm:w-full px-4 sm:px-6 lg:px-6 bg-gray-200 border-r border-gray-400" v-show="activeTab === 'details'">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="mt-6 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-text class="sm:col-span-6"
                                label="First Name"
                                placeholder="First Name"
                                name="first"
                                v-model="item.first"
                                rules="required"
                                :required="true" />
                            <input-text class="sm:col-span-6"
                                label="Last Name"
                                placeholder="Last Name"
                                name="last"
                                v-model="item.last"
                                rules="required"
                                :required="true" />
                            <input-text class="sm:col-span-4"
                                label="Email"
                                placeholder="Email"
                                name="email"
                                v-model="item.email"
                                rules="required|email"
                                :required="true" />
                            <input-text class="sm:col-span-4"
                                label="Mobile"
                                placeholder="Mobile"
                                name="mobile"
                                v-model="item.mobile"
                                :required="false"
                                :mask="'#### ### ###'"/>
                            <input-text class="sm:col-span-4"
                                label="Phone"
                                placeholder="Phone"
                                name="phone"
                                v-model="item.phone"
                                :required="false" />
                            <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">Password</h2>
                            <input-text class="sm:col-span-6"
                                type="password"
                                name="password"
                                label="Password"
                                :placeholder="type !== 'New' ? 'Leave empty for no change' : 'Password'"
                                v-model="item.password"
                                :rules="type === 'New' ? 'required|password:@confirm' : 'password:@confirm'"
                                :required="type === 'New'" />
                            <input-text class="sm:col-span-6"
                                type="password"
                                name="confirm"
                                label="Confirm Password"
                                provider-name="confirm"
                                :placeholder="type !== 'New' ? 'Leave empty for no change' : 'Confirm Password'"
                                :rules="type === 'New' ? 'required' : ''"
                                :required="type === 'New'" />
                        </div>
                    </ValidationObserver>
                </div>
                <div class="md:w-3/4 sm:w-full px-4 sm:px-6 lg:px-6 bg-gray-200 border-r border-gray-400" v-show="activeTab === 'groups'">
                    <spinner line-fg-color="#922C49" v-if="groupsLoading" class="w-full mt-4 flex-col"></spinner>
                    <div v-if="!groupsLoading">
                        <p class="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 text-sm mt-4">
                            Adding a user to specific groups allows them to inherit access rights & permissions from those groups. It also allows you to group similar users for use in setting up notifications.
                        </p>
                        <input type="text" v-model="searchGroups" value="" placeholder="Filter by name..." class="form-input w-full my-3 text-sm" />
                        <table class="min-w-full text-xs mb-10">
                            <thead>
                            <tr>
                                <th class="table-head-cell w-12"><input type="checkbox" value="all" name="selectAll" v-model="selectAllGroups"></th>
                                <th class="table-head-cell pl-0">Name</th>
                                <th class="table-head-cell">Description</th>
                            </tr>
                            </thead>
                            <tbody v-if="groups">
                            <tr v-for="(item, index) in groups" v-bind:key="index">
                                <td class="table-body-cell w-12 py-2" :class="[index % 2 !== 0 ? 'even' : 'odd' ]"><input type="checkbox" :value="item.id" v-model="selectedGroups"></td>
                                <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.name }}</td>
                                <td class="table-body-cell" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.description }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="md:w-3/4 sm:w-full px-4 sm:px-6 lg:px-6 bg-gray-200 border-r border-gray-400" v-show="activeTab === 'access'">
                    <spinner line-fg-color="#922C49" v-if="sectionsLoading" class="w-full mt-4 flex-col"></spinner>
                    <div v-if="!sectionsLoading">
                        <p class="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 text-sm mt-4">
                            Access Controls give you the ability to grant access to different parts of the system. By default all pages and sections are restricted. The below Access Controls will be specific to this user. This user will also inherit access controls from any groups they have been assigned (inherited Access Controls <strong>will not</strong> be displayed below)
                        </p>
                        <input type="text" v-model="searchSections" value="" placeholder="Filter by name..." class="form-input w-full my-3 text-sm" />
                        <table class="min-w-full text-xs mb-10">
                            <thead>
                            <tr>
                                <th class="table-head-cell w-12"><input type="checkbox" value="all" name="selectAll" v-model="selectAllSections"></th>
                                <th class="table-head-cell pl-0">Access Control</th>
                            </tr>
                            </thead>
                            <tbody v-if="sections">
                            <tr v-for="(item, index) in sections" v-bind:key="index">
                                <td class="table-body-cell w-12 py-2" :class="[index % 2 !== 0 ? 'even' : 'odd' ]"><input type="checkbox" :value="item.id" v-model="selectedSections"></td>
                                <td class="table-body-cell" :class="[ {'pl-0': item.level === 1, 'pl-4': item.level === 2}, index % 2 !== 0 ? 'even' : 'odd' ]"><span class="indent-tick" v-if="item.level !== 1">|__</span> {{ item.name }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="md:w-3/4 sm:w-full px-4 sm:px-6 lg:px-6 bg-gray-200 border-r border-gray-400" v-show="activeTab === 'permissions'">
                    <spinner line-fg-color="#922C49" v-if="permissionsLoading" class="w-full mt-4 flex-col"></spinner>
                    <div v-if="!permissionsLoading">
                        <p class="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 text-sm mt-4">
                            The below permissions will be specific to this user. This user will also inherit permissions from any groups they have been assigned (inherited permissions <strong>will not</strong> be displayed below)
                        </p>
                        <input type="text" v-model="searchPermissions" value="" placeholder="Filter by name..." class="form-input w-full my-3 text-sm" />
                        <table class="min-w-full text-xs mb-10">
                            <thead>
                            <tr>
                                <th class="table-head-cell w-12"><input type="checkbox" value="all" name="selectAll" v-model="selectAllPermissions"></th>
                                <th class="table-head-cell pl-0">Name</th>
                                <th class="table-head-cell">Description</th>
                            </tr>
                            </thead>
                            <tbody v-if="sections">
                            <tr v-for="(item, index) in permissions" v-bind:key="index">
                                <td class="table-body-cell w-12 py-2" :class="[index % 2 !== 0 ? 'even' : 'odd' ]"><input type="checkbox" :value="item.id" v-model="selectedPermissions"></td>
                                <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.name }}</td>
                                <td class="table-body-cell" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.description }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="md:w-1/4 sm:w-full px-4 sm:px-6 lg:px-6">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="mt-6 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-toggle class="sm:col-span-3"
                                label="Active"
                                name="active"
                                v-model="item.active"
                                :required="false" />
                        </div>
                    </ValidationObserver>
                </div>
            </div>
        </form>
    </ValidationObserver>
</template>

<script>
    import axios from "axios";
    import FormSetup from "../../../mixins/Tabs";
    import FormButtons from "../../../mixins/FormButtons";

    export default {
        name: "ContactForm",
        props: {
            id: {required: false},
            type: {required: true},
            return: {required: false, default: '/configuration/users'},
            page: {required: false, default: 'users'}
        },
        mixins: [
            FormSetup,
            FormButtons
        ],
        data: function () {
            return {
                resource: 'users',
                prefix: 'configuration',
                pageActions: {
                    buttons: [
                        {
                            label: 'Save',
                            icon: 'save',
                            iconClass: 'text-green-600',
                            type: 'method',
                            method: 'save',
                        },
                        {
                            label: 'View',
                            icon: 'eye',
                            iconClass: 'text-blue-600',
                            type: 'method',
                            method: 'view',
                            prefix: '/configuration',
                            display: this.type === "Edit"
                        },
                        {
                            label: 'Cancel',
                            icon: 'times',
                            iconClass: 'text-red-600',
                            type: 'method',
                            method: 'cancel',
                        }
                    ],
                    additional: []
                },
                tabs: [
                    {
                        name: 'Details',
                        slug: 'details'
                    },
                    {
                        name: 'Groups',
                        slug: 'groups'
                    },
                    {
                        name: 'Access',
                        slug: 'access'
                    },
                    {
                        name: 'Permissions',
                        slug: 'permissions'
                    }
                ],
                startTab: null,
                loading: true,
                submitted: false,
                errorMessage: '',
                item: {
                    tags: []
                },
                S3URL: process.env.VUE_APP_S3_URL,
                confirm: '',
                searchPermissions: '',
                originalPermissions: [],
                selectedPermissions: [],
                permissionsLoading: true,
                searchSections: '',
                originalSections: [],
                selectedSections: [],
                sectionsLoading: true,
                searchGroups: '',
                originalGroups: [],
                selectedGroups: [],
                groupsLoading: true
            }
        },
        methods: {
            getData: function () {
                this.$Progress.start();

                axios.get(process.env.VUE_APP_API_URL + '/users/' + this.id, {
                    params: {
                        with: 'permissions,sections,groups',
                        combined: 'false'
                    }
                })
                    .then(response => {
                        this.item = response.data;

                        if (response.data.permissions) {
                            let selected = [];
                            response.data.permissions.forEach(function (permission) {
                                selected.push(permission.id);
                            });

                            this.selectedPermissions = selected;
                        }

                        if (response.data.sections) {
                            let selected = [];
                            response.data.sections.forEach(function (section) {
                                selected.push(section.id);
                            });

                            this.selectedSections = selected;
                        }

                        if (response.data.groups) {
                            let selected = [];
                            response.data.groups.forEach(function (group) {
                                selected.push(group.id);
                            });

                            this.selectedGroups = selected;
                        }

                        this.$Progress.finish();
                        this.loading = false;
                    })
                    .catch(() => {
                        this.$Progress.finish();
                        this.loading = false;
                    });
            },
            submitForm: function () {
                this.submitted = false;

                this.$refs.form.validate().then(success => {
                    let _method;
                    let _url;
                    if (!success) {
                        this.submitted = true;
                        return;
                    }

                    this.submitted = true;

                    this.$store.commit('TOGGLE_BACKDROP', {open: true});
                    this.errorMessage = '';

                    if (this.id) {
                        _url = process.env.VUE_APP_API_URL + '/users/' + this.id;
                        _method = 'PUT';
                    } else if (this.item.id) {
                        _url = process.env.VUE_APP_API_URL + '/users/' + this.item.id;
                        _method = 'PUT';
                    } else {
                        _url = process.env.VUE_APP_API_URL + '/users';
                        _method = 'POST';
                    }

                    this.item.sections = this.selectedSections;
                    this.item.permissions = this.selectedPermissions;
                    this.item.groups = this.selectedGroups;

                    axios({
                        method: _method,
                        url: _url,
                        data: this.item
                    }).then(response => {
                        if (response.data.success) {
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});

                            if (this.saveClose) {
                                if (this.return) {
                                    this.$router.push(this.return);
                                } else {
                                    this.$router.push('/configuration/users');
                                }
                            } else {
                                this.$router.push('/configuration/users/'+response.data.data.id+'/view');
                                //this.item.id = response.data.data.id;
                            }

                            this.$notify({
                                group: 'main',
                                type: 'success',
                                title: 'Success',
                                text: response.data.message,
                                duration: 5000
                            });

                        } else {
                            if (response.data.message) {
                                this.errorMessage = response.data.message;
                            }
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});
                        }
                    }).catch(error => {
                        if (error.response.data.message) {
                            this.errorMessage = error.response.data.message;
                        } else {
                            this.errorMessage = 'An error occurred. Please contact the System Administrator.';
                        }

                        this.$store.commit('TOGGLE_BACKDROP', {open: false});
                    });
                });
            },
            cancel: function () {
                this.$router.push(this.return);
            },
            methodHandler: function (method) {
                this[method]();
            },
            actionHandler: function (data) {
                this[data.method](data);
            },
            getSections: function(){
                axios.get(process.env.VUE_APP_API_URL + '/sections').then(response => {
                    this.originalSections = response.data.map(function (section) {
                        return { id: section.id, name: section.name, description: section.description, level: section.level };
                    });
                    this.sectionsLoading = false;
                });
            },
            getPermissions: function(){
                axios.get(process.env.VUE_APP_API_URL + '/permissions', { params: { active: 1, standard: 0  } }).then(response => {
                    this.originalPermissions = response.data.map(function (permission) {
                        return { id: permission.id, name: permission.name, description: permission.description };
                    });
                    this.permissionsLoading = false;
                });
            },
            getGroups: function(){
                axios.get(process.env.VUE_APP_API_URL + '/groups', { params: { active: 1 } }).then(response => {
                    this.originalGroups = response.data.map(function (group) {
                        return { id: group.id, name: group.name, description: group.description };
                    });
                    this.groupsLoading = false;
                });
            }
        },
        beforeMount() {
            this.getGroups();
            this.getSections();
            this.getPermissions();
        },
        mounted() {
            if (this.type === "New") {
                this.loading = false;
                this.item.active = true;
                this.$Progress.finish();
                // Se defaults here
            } else if (this.type === "Edit") {
                this.pageActions.additional.push({
                    label: 'Delete',
                    icon: 'trash-alt',
                    type: 'method',
                    method: 'deleteModal',
                    iconClass: 'text-red-500',
                    display: !!(this.$store.getters.permissions.indexOf('users.destroy') !== -1 || this.$store.getters.nimdarepus)
                });
                this.getData();
            }

            const params = new URLSearchParams(window.location.search)

            let windowLocationHash = window.location.hash.replace('#', '');

            if (windowLocationHash) {
                this.startTab = windowLocationHash;
            }
        },
        computed: {
            groups () {
                if (this.searchGroups) {
                    return this.originalGroups.filter(item => item.name.toUpperCase().includes(this.searchGroups.toUpperCase()));
                }
                return this.originalGroups;
            },
            permissions () {
                if (this.searchPermissions) {
                    return this.originalPermissions.filter(item => item.name.toUpperCase().includes(this.searchPermissions.toUpperCase()));
                }
                return this.originalPermissions;
            },
            sections () {
                if (this.searchSections) {
                    return this.originalSections.filter(item => item.name.toUpperCase().includes(this.searchSections.toUpperCase()));
                }
                return this.originalSections;
            },
            selectAllGroups: {
                get: function () {
                    return this.groups ? this.selectedGroups.length === this.groups.length : false;
                },
                set: function (value) {
                    let selected = [];

                    if (value) {
                        this.groups.forEach(function (group) {
                            selected.push(group.id);
                        });
                    }

                    this.selectedGroups = selected;
                }
            },
            selectAllSections: {
                get: function () {
                    return this.sections ? this.selectedSections.length === this.sections.length : false;
                },
                set: function (value) {
                    let selected = [];

                    if (value) {
                        this.sections.forEach(function (section) {
                            selected.push(section.id);
                        });
                    }

                    this.selectedSections = selected;
                }
            },
            selectAllPermissions: {
                get: function () {
                    return this.permissions ? this.selectedPermissions.length === this.permissions.length : false;
                },
                set: function (value) {
                    let selected = [];

                    if (value) {
                        this.permissions.forEach(function (section) {
                            selected.push(section.id);
                        });
                    }

                    this.selectedPermissions = selected;
                }
            },
        }
    }
</script>

<style scoped>

</style>
